/**
 * rfq-page.js — Request for Quote page logic.
 *
 * Reads the quote cart from localStorage, renders items,
 * handles edit/remove, and submits the aggregated quote request.
 *
 * Requires: quote-cart.js, add-to-quote-modal.js (for editing)
 */
(function () {
  'use strict';

  var THROTTLE_KEY    = 'aat_quote_submissions';
  var THROTTLE_LIMIT  = 5;
  var THROTTLE_WIN_MS = 30 * 60 * 1000;

  // ── Helpers ────────────────────────────────────────────────────────────────

  function esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function formatDecoration(d) {
    var parts = [d.type, d.location];
    if (d.type === 'Print' && d.colors)
      parts.push(d.colors + ' color' + (parseInt(d.colors, 10) !== 1 ? 's' : ''));
    if (d.type === 'Embroidery' && d.size)
      parts.push(d.size + '"');
    return parts.filter(Boolean).join(' — ');
  }

  function formatDate(str) {
    try {
      return new Date(str + 'T12:00:00').toLocaleDateString('en-US',
        { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) { return str; }
  }

  function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  // ── File attachments ───────────────────────────────────────────────────────

  var attachedFiles = [];

  function renderFileList(listEl) {
    if (!listEl) return;
    listEl.hidden = attachedFiles.length === 0;
    listEl.innerHTML = attachedFiles.map(function (f, i) {
      return '<li class="quote-files__item">' +
        '<span class="quote-files__name">' + esc(f.name) + '</span>' +
        '<span class="quote-files__size">' + formatBytes(f.size) + '</span>' +
        '<button type="button" class="quote-files__remove" data-remove-file="' + i + '" aria-label="Remove ' + esc(f.name) + '">&times;</button>' +
        '</li>';
    }).join('');
  }

  function initFileUploads(form) {
    var input  = form.querySelector('[data-rfq-files-input]');
    var listEl = form.querySelector('[data-rfq-files-list]');
    if (!input || !listEl) return;

    input.addEventListener('change', function () {
      Array.prototype.forEach.call(input.files, function (file) {
        var dup = attachedFiles.some(function (f) {
          return f.name === file.name && f.size === file.size && f.lastModified === file.lastModified;
        });
        if (!dup) attachedFiles.push(file);
      });
      // Clear the native input so the same file can be re-selected after removal
      input.value = '';
      renderFileList(listEl);
    });

    listEl.addEventListener('click', function (evt) {
      var btn = evt.target.closest('[data-remove-file]');
      if (!btn) return;
      attachedFiles.splice(parseInt(btn.dataset.removeFile, 10), 1);
      renderFileList(listEl);
    });
  }

  // ── Throttle ───────────────────────────────────────────────────────────────

  function checkThrottle() {
    try {
      var now  = Date.now();
      var subs = JSON.parse(localStorage.getItem(THROTTLE_KEY) || '[]');
      var recent = subs.filter(function (t) { return now - t < THROTTLE_WIN_MS; });
      if (recent.length < THROTTLE_LIMIT) return null;
      var oldest = Math.min.apply(null, recent);
      return Math.ceil((THROTTLE_WIN_MS - (now - oldest)) / 60000);
    } catch (e) { return null; }
  }

  function recordSubmission() {
    try {
      var now  = Date.now();
      var subs = JSON.parse(localStorage.getItem(THROTTLE_KEY) || '[]')
        .filter(function (t) { return now - t < THROTTLE_WIN_MS; });
      subs.push(now);
      localStorage.setItem(THROTTLE_KEY, JSON.stringify(subs));
    } catch (e) {}
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  function renderItems() {
    var items     = window.QuoteCart.get();
    var listEl    = document.querySelector('[data-rfq-cart-items]');
    var emptyEl   = document.querySelector('[data-rfq-cart-empty]');
    var addMoreEl = document.querySelector('[data-rfq-add-more]');
    var submitBtn = document.querySelector('[data-rfq-submit]');

    if (!listEl) return;

    var hasItems = items.length > 0;
    if (emptyEl)   emptyEl.hidden  = hasItems;
    if (addMoreEl) addMoreEl.hidden = !hasItems;

    listEl.innerHTML = '';
    items.forEach(function (item) {
      listEl.appendChild(buildItemCard(item));
    });
  }

  function buildItemCard(item) {
    var el = document.createElement('div');
    el.className = 'rfq-item';
    el.dataset.rfqItem = item.cartId;

    var imgHtml = item.imageUrl
      ? '<img class="rfq-item__img" src="' + esc(item.imageUrl) + '" alt="" loading="lazy">'
      : '<div class="rfq-item__img rfq-item__img--placeholder"></div>';

    var colorLine = item.colorName
      ? '<span class="rfq-item__meta-label">Color:</span> ' + esc(item.colorName) + ' &nbsp;·&nbsp; '
      : '';

    var decsHtml = '';
    if (item.decorations && item.decorations.length) {
      decsHtml = '<ul class="rfq-item__decorations">' +
        item.decorations.map(function (d) {
          return '<li>' + esc(formatDecoration(d)) + '</li>';
        }).join('') +
        '</ul>';
    }

    var productLink = item.productUrl
      ? '<a href="' + esc(item.productUrl) + '" class="rfq-item__title">' + esc(item.productTitle) + '</a>'
      : '<span class="rfq-item__title">' + esc(item.productTitle) + '</span>';

    el.innerHTML =
      imgHtml +
      '<div class="rfq-item__body">' +
        productLink +
        '<p class="rfq-item__meta">' +
          colorLine +
          '<span class="rfq-item__meta-label">Qty:</span> ' + esc(item.quantity) +
        '</p>' +
        (item.description
          ? '<p class="rfq-item__description">' + esc(item.description) + '</p>'
          : '') +
        decsHtml +
        '<div class="rfq-item__actions">' +
          '<button class="rfq-item__action rfq-item__action--edit" data-rfq-edit="' + esc(item.cartId) + '">Edit</button>' +
          '<button class="rfq-item__action rfq-item__action--remove" data-rfq-remove="' + esc(item.cartId) + '">Remove</button>' +
        '</div>' +
      '</div>';

    return el;
  }

  // ── Event delegation for edit / remove ────────────────────────────────────

  function onListClick(evt) {
    var editBtn   = evt.target.closest('[data-rfq-edit]');
    var removeBtn = evt.target.closest('[data-rfq-remove]');

    if (editBtn) {
      var cartId = editBtn.dataset.rfqEdit;
      var item   = window.QuoteCart.get().find(function (it) { return it.cartId === cartId; });
      if (item && window._atqModal) window._atqModal.openForEdit(item);
    }

    if (removeBtn) {
      window.QuoteCart.remove(removeBtn.dataset.rfqRemove);
    }
  }

  // ── Submission ─────────────────────────────────────────────────────────────

  function initForm() {
    var form      = document.querySelector('[data-rfq-form]');
    if (!form) return;

    var proxyUrl        = form.dataset.proxyUrl;
    var turnstileSiteKey = form.dataset.turnstileSiteKey;
    // Submit area lives outside the <form> (linked via form="RFQForm"), so search the document
    var submitBtn       = document.querySelector('[data-rfq-submit]');
    var submitText      = document.querySelector('[data-rfq-submit-text]');
    var spinner         = document.querySelector('[data-rfq-spinner]');
    var errorEl         = document.querySelector('[data-rfq-error]');
    var turnstileEl     = document.querySelector('[data-rfq-turnstile]');

    initFileUploads(form);

    // Init Turnstile
    if (turnstileSiteKey && turnstileEl) {
      if (!document.getElementById('cf-turnstile-script')) {
        var s = document.createElement('script');
        s.id  = 'cf-turnstile-script';
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        s.async = s.defer = true;
        document.head.appendChild(s);
      }
      turnstileEl.setAttribute('data-sitekey', turnstileSiteKey);
      turnstileEl.classList.add('cf-turnstile');
    }

    form.addEventListener('submit', async function (evt) {
      evt.preventDefault();

      if (!form.checkValidity()) { form.reportValidity(); return; }

      var items = window.QuoteCart.get();

      var minutesLeft = checkThrottle();
      if (minutesLeft !== null) {
        errorEl.textContent = 'Too many submissions. Please try again in ' +
          minutesLeft + ' minute' + (minutesLeft !== 1 ? 's' : '') + '.';
        errorEl.hidden = false;
        return;
      }

      if (!proxyUrl) {
        errorEl.textContent = 'Quote submission is not configured yet. Please contact us directly.';
        errorEl.hidden = false;
        return;
      }

      var turnstileToken = '';
      var tokenInput = document.querySelector('[name="cf-turnstile-response"]');
      if (tokenInput) turnstileToken = tokenInput.value;
      if (turnstileSiteKey && !turnstileToken) {
        errorEl.textContent = 'Please complete the security check.';
        errorEl.hidden = false;
        return;
      }

      errorEl.hidden = true;
      submitBtn.disabled = true;
      submitText.hidden  = true;
      if (spinner) { spinner.hidden = false; spinner.removeAttribute('aria-hidden'); }

      var formData = new FormData(form);

      var totalQty = items.reduce(function (sum, it) {
        return sum + (parseInt(it.quantity, 10) || 0);
      }, 0);

      var jobs = items.map(function (it) {
        return {
          quantity:    it.quantity,
          description: it.description,
          decorations: it.decorations || [],
          imprintables: [{
            imprintable_id: it.crmProductId,
            color_id:       it.colorId,
            color_name:     it.colorName
          }]
        };
      });

      var imprintables = items.map(function (it) {
        return {
          imprintable_id: it.crmProductId,
          color_id:       it.colorId,
          color_name:     it.colorName,
          quantity:       it.quantity
        };
      });

      var attribution = window.AATAttribution;

      var quoteRequest = {
        name:             formData.get('name'),
        email:            formData.get('email'),
        organization:     formData.get('organization'),
        phone_number:     formData.get('phone_number'),
        approx_quantity:  String(totalQty),
        date_needed:      formData.get('date_needed'),
        description:      formData.get('notes') || '',
        ip_address:       null,
        domain:           window.location.hostname,
        source:           'shopify-www',
        campaign_codes:   attribution ? attribution.campaignCodes() : '',
        from_page_slug:   'pages/request-for-quote',
        job_description:  jobs,
        quote_request_imprintables_attributes: imprintables
      };
      // utm_* / click IDs from the visitor's original campaign landing
      if (attribution) Object.assign(quoteRequest, attribution.quoteFields());

      var body = new FormData();
      body.append('quote_request', JSON.stringify(quoteRequest));
      if (turnstileToken) body.append('turnstile_token', turnstileToken);
      attachedFiles.forEach(function (file) {
        body.append('customer_uploads[]', file, file.name);
      });

      try {
        var controller = new AbortController();
        var timeoutMs  = attachedFiles.length ? 120000 : 30000;
        var timeoutId  = setTimeout(function () { controller.abort(); }, timeoutMs);

        // No Content-Type header — the browser sets multipart/form-data with boundary
        var res = await fetch(proxyUrl, {
          method:  'POST',
          body:    body,
          signal:  controller.signal
        });
        clearTimeout(timeoutId);

        if (!res.ok) {
          var body = {};
          try { body = await res.json(); } catch (e) {}
          throw new Error(body.error || 'Server error (' + res.status + ')');
        }

        await res.json();
        recordSubmission();

        var freshworks = window.AATFreshworks;
        if (freshworks) {
          freshworks.identify({
            name:         formData.get('name'),
            email:        formData.get('email'),
            phone:        formData.get('phone_number'),
            organization: formData.get('organization')
          });
          freshworks.trackEvent('Quote requested', formData.get('email'), {
            form:        'Request for quote page',
            products:    items.map(function (it) { return it.productTitle; }).filter(Boolean).join(', '),
            item_count:  items.length,
            quantity:    totalQty,
            date_needed: formData.get('date_needed'),
            page:        window.location.pathname
          });
        }

        // Publish a successful quote submission for analytics integrations.
        // Guarded so an analytics failure can never surface as a submission error.
        try {
          if (window.Shopify && window.Shopify.analytics &&
              typeof window.Shopify.analytics.publish === 'function') {
            var published = window.Shopify.analytics.publish('quote_submitted', {
              form_type:  'request_for_quote',
              item_count: items.length,
              quantity:   totalQty
            });
            if (published && typeof published.catch === 'function') published.catch(function () {});
          }
        } catch (e) {}

        window.QuoteCart.clear();
        showSuccess(formData, items);

      } catch (err) {
        // Turnstile tokens are single-use: reset so a retry gets a fresh token
        if (window.turnstile) { try { window.turnstile.reset(); } catch (e) {} }
        var msg = err.name === 'AbortError'
          ? 'Request timed out. Please try again.'
          : (err.message || 'Something went wrong. Please try again or contact us directly.');
        errorEl.textContent = msg;
        errorEl.hidden      = false;
        submitBtn.disabled  = false;
        submitText.hidden   = false;
        if (spinner) { spinner.hidden = true; spinner.setAttribute('aria-hidden', 'true'); }
      }
    });
  }

  function showSuccess(formData, items) {
    var page = document.querySelector('[data-rfq-page]');
    if (!page) return;

    var itemRows = items.map(function (it) {
      var decs = (it.decorations || []).map(function (d) {
        return '<li>' + esc(formatDecoration(d)) + '</li>';
      }).join('');
      return '<div class="quote-success__row">' +
        '<dt class="quote-success__dt">' + esc(it.productTitle) + '</dt>' +
        '<dd class="quote-success__dd">' +
          (it.colorName ? 'Color: ' + esc(it.colorName) + ' &nbsp;·&nbsp; ' : '') +
          'Qty: ' + esc(it.quantity) +
          (decs ? '<ul class="rfq-success__decs">' + decs + '</ul>' : '') +
        '</dd>' +
      '</div>';
    }).join('');

    if (!itemRows) {
      itemRows = '<div class="quote-success__row">' +
        '<dd class="quote-success__dd">No items were added to this request.</dd>' +
      '</div>';
    }

    var phone = (formData.get('phone_number') || '').trim();
    var notes = (formData.get('notes') || '').trim();
    var fileNames = attachedFiles.map(function (f) { return f.name; }).join(', ');

    page.innerHTML =
      '<div class="quote-success">' +
        '<div class="quote-success__icon" aria-hidden="true">✓</div>' +
        '<h2 class="quote-success__title">Quote Request Submitted!</h2>' +
        '<p class="quote-success__subtitle">We\'ve received your request and will be in touch shortly.</p>' +
        '<dl class="quote-success__summary">' +
          '<div class="quote-success__row">' +
            '<dt class="quote-success__dt">Name</dt>' +
            '<dd class="quote-success__dd">' + esc(formData.get('name')) + '</dd>' +
          '</div>' +
          '<div class="quote-success__row">' +
            '<dt class="quote-success__dt">Email</dt>' +
            '<dd class="quote-success__dd">' + esc(formData.get('email')) + '</dd>' +
          '</div>' +
          (phone
            ? '<div class="quote-success__row"><dt class="quote-success__dt">Phone</dt>' +
              '<dd class="quote-success__dd">' + esc(phone) + '</dd></div>'
            : '') +
          (formData.get('date_needed')
            ? '<div class="quote-success__row"><dt class="quote-success__dt">Date needed</dt>' +
              '<dd class="quote-success__dd">' + esc(formatDate(formData.get('date_needed'))) + '</dd></div>'
            : '') +
          '<div class="quote-success__row"><dt class="quote-success__dt">Attached files</dt>' +
            '<dd class="quote-success__dd">' + (fileNames ? esc(fileNames) : 'None') + '</dd></div>' +
        '</dl>' +
        (notes
          ? '<div class="quote-success__section">' +
              '<p class="quote-success__section-label">Additional notes</p>' +
              '<p class="quote-success__message">' + esc(notes) + '</p>' +
            '</div>'
          : '') +
        '<h3 class="quote-success__section-label" style="margin-top:24px">Items</h3>' +
        '<dl class="quote-success__summary">' + itemRows + '</dl>' +
      '</div>';
  }

  // ── Boot ───────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    renderItems();
    initForm();

    var listEl = document.querySelector('[data-rfq-cart-items]');
    if (listEl) listEl.addEventListener('click', onListClick);

    window.addEventListener('quotecart:change', renderItems);
  });
})();
